import * as React from 'react';

import {
  CopyOutlined as FileCopyOutlined,
  DownOutlined as ExpandMore,
  ExclamationCircleOutlined as Error,
  MoreOutlined as MoreHoriz
} from '@ant-design/icons';
import { Button, Col, Collapse, Pagination, Row, Skeleton, Tag, Tooltip } from 'antd';

import Truncate from 'components/Truncate';
import PermissionHide from 'v2/components/Shared/PermissionHide';
import { WebhookStatus, WebhookStatusAlias, WebhookTypeAlias } from 'v2/interfaces/webhook2';
import type { Webhook } from 'v2/interfaces/webhook2';

import { Actions } from './Actions';
import { Filter } from './Filter';
import { useWebhookContext } from '../context';
import { History } from '../History';
import WebhookTour from '../Tour';

const WEBHOOK_STATUS_COLOR: Record<string, string> = {
  active: 'text-success',
  failing: 'text-error font-semibold',
  paused: 'text-[#039BE5]'
};

const ITEMS_PER_PAGE = 20;
const TOOLBAR_HEIGHT = 64;
const SCROLL_DELAY = 50;

export const List = () => {
  const { loading, webhooks, filter, isActionsOpen, listWebhooks, setSelectedWebhook, setAnchorEl, setIsActionsOpen } =
    useWebhookContext();

  const [url, setUrl] = React.useState<string>();
  const [page, setPage] = React.useState(1);

  const handleClickActions = (e: React.MouseEvent<HTMLElement>, w: Webhook) => {
    e.stopPropagation();
    setSelectedWebhook(w);
    setAnchorEl(e.currentTarget);
    setIsActionsOpen(true);
  };

  const showConfiguredProduct = (products: number[]) => {
    if (products.includes(-1)) {
      return ['Todos'];
    }

    const firstThree = products.slice(0, 3).map(String);

    if (firstThree.length < 2) {
      return firstThree;
    }

    firstThree.push(`+${products.length - 3}`);

    return firstThree;
  };

  const handleCopy = (e: React.SyntheticEvent, w: Webhook) => {
    e.stopPropagation();
    navigator.clipboard.writeText(w.url);
    setUrl(w.url);

    setTimeout(() => {
      setUrl(null);
    }, 2000);
  };

  const handleChangePage = (value: number) => {
    setPage(value);

    const filtersElement = document.getElementById('webhook-filters');

    if (!filtersElement) {
      return;
    }

    setTimeout(() => {
      window.scroll({
        top: filtersElement.getBoundingClientRect().top + window.scrollY - TOOLBAR_HEIGHT,
        behavior: 'smooth'
      });
    }, SCROLL_DELAY);
  };

  React.useEffect(() => {
    listWebhooks();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (page !== 1) {
      setPage(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const urlToLowerCase = filter?.url?.toLowerCase();

  const filtered = React.useMemo(
    () =>
      webhooks
        ?.filter(w =>
          !urlToLowerCase
            ? true
            : w?.url?.toLowerCase().includes(urlToLowerCase) || w.description?.toLowerCase().includes(urlToLowerCase)
        )
        .filter(f => (filter?.status === 'all' ? true : f.state === filter?.status))
        .filter(g => (filter?.type === 'all' ? true : g.type === filter?.type)),
    [filter?.status, filter?.type, urlToLowerCase, webhooks]
  );

  const paginatedData = React.useMemo(
    () => filtered?.slice((page - 1) * ITEMS_PER_PAGE, page * ITEMS_PER_PAGE),
    [filtered, page]
  );

  if (loading) {
    return (
      <>
        {[...Array(10)].map((_, n) => (
          <Skeleton key={n} active paragraph={{ rows: 2 }} className='mt-3' />
        ))}
      </>
    );
  }

  return (
    <>
      <Row gutter={16}>
        <Col span={24}>
          <Filter />
        </Col>

        <Col span={24}>
          {filtered?.length === 0 && <p className='mt-3 p-6 text-center'>Nenhum webhook encontrado</p>}

          {paginatedData.map((w, index) => {
            return (
              <Collapse
                key={w._id}
                destroyOnHidden
                expandIconPosition='end'
                expandIcon={({ isActive }) => (
                  <Button
                    type='text'
                    shape='circle'
                    id={index === 0 ? 'webhook-v2-history-action' : undefined}
                    icon={<ExpandMore rotate={isActive ? 180 : 0} />}
                  />
                )}
                className='mt-3! rounded-none! p-2 [&_.ant-collapse-content-box]:p-0! [&_.ant-collapse-header]:items-center!'
                style={{
                  boxShadow: '0px 0px 1px 0px #0000001f'
                }}
                items={[
                  {
                    key: w._id,
                    label: (
                      <div className='flex w-full items-center justify-between'>
                        <Row gutter={[16, 16]} align='middle' className='w-full'>
                          <Col xs={24} md={24} lg={8}>
                            <div>
                              <b className='wrap-break-word'>{w.description}</b>
                              <Row align='middle'>
                                <Col>
                                  <span className={`text-sm font-medium ${WEBHOOK_STATUS_COLOR[w.state] || ''}`}>
                                    {WebhookStatusAlias[w.state]}
                                  </span>
                                </Col>
                                <Col>
                                  {w.state === WebhookStatus.failing && (
                                    <Tooltip title='Sua URL retornou erro diversas vezes para um mesmo evento, por esse motivo, sua URL foi marcada como falhando e salvamos os eventos subsequentes em backup, corrija sua integração e os eventos serão reenviados.'>
                                      <Button type='text' size='small' icon={<Error className='text-[16px]' />} />
                                    </Tooltip>
                                  )}
                                </Col>
                              </Row>
                              <Row align='middle'>
                                <span className='text-[15px] font-medium'>
                                  Produto(s):{' '}
                                  {showConfiguredProduct(w.product_id).map(p => (
                                    <Tag className='border border-[#eee] text-[#061D37]' key={p}>
                                      {p}
                                    </Tag>
                                  ))}
                                </span>
                              </Row>
                            </div>
                          </Col>
                          <Col xs={24} md={24} lg={10}>
                            <span className='flex items-center gap-2 text-sm'>
                              <Truncate count={40} final='...' title={w.url}>
                                {w.url}
                              </Truncate>
                              <Tooltip title={url === w.url ? 'Copiado!' : 'Clique para copiar'}>
                                <FileCopyOutlined onClick={e => handleCopy(e, w)} />
                              </Tooltip>
                            </span>
                          </Col>
                          <Col xs={24} md={24} lg={4}>
                            <span className='text-sm'>{WebhookTypeAlias[w.type as keyof typeof WebhookTypeAlias]}</span>
                          </Col>
                        </Row>
                      </div>
                    ),
                    extra: (
                      <PermissionHide action={'myeduzz:webhook:editar'}>
                        <Tooltip title='Ações'>
                          <Button
                            type='text'
                            shape='circle'
                            icon={<MoreHoriz />}
                            onClick={e => handleClickActions(e, w)}
                            aria-controls={isActionsOpen ? 'account-menu' : undefined}
                            aria-haspopup='true'
                            aria-expanded={isActionsOpen ? 'true' : undefined}
                          />
                        </Tooltip>
                      </PermissionHide>
                    ),
                    children: <History webhook={w} />
                  }
                ]}
              />
            );
          })}
        </Col>
      </Row>

      {filtered?.length > ITEMS_PER_PAGE && (
        <Row>
          <Col span={24}>
            <div className='my-6 flex w-full items-center justify-center'>
              <Pagination
                total={filtered?.length}
                pageSize={ITEMS_PER_PAGE}
                current={page}
                onChange={handleChangePage}
              />
            </div>
          </Col>
        </Row>
      )}

      <Actions />

      <WebhookTour />
    </>
  );
};
